import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateFnsLocale } from '../i18n/dateFnsLocale';
import { loadUserFromStorage } from '../utils/authStorage';
import {
    dateFormatPattern,
    formatDateDisplay,
    formatDateTimeDisplay,
    getDatePreferencesSnapshot,
    parseDateInput,
    pickerDateFormat,
    resolveDateFormat,
    type DateFormatOrder,
    type DateFormatPreference,
    type DateFormatPrefs,
    type DateDisplayStyle,
} from '../utils/dates';

function readStoredDateFormat(): DateFormatPreference {
    try {
        const d = loadUserFromStorage();
        const raw = (d?.user as { dateFormat?: unknown } | undefined)?.dateFormat;
        if (raw === 'MDY' || raw === 'DMY' || raw === 'YMD') return raw;
        return null;
    } catch {
        return null;
    }
}

/**
 * Portal date preferences: effective order, formatters, and parse helpers.
 * Reads uiLanguage from i18n + dateFormat from localStorage user payload.
 */
export function useDatePreferences() {
    const { i18n } = useTranslation();
    const uiLanguage = i18n.language || 'en-US';
    const storedFormat = readStoredDateFormat();

    const dateFormat = useMemo(
        () => resolveDateFormat(uiLanguage, storedFormat),
        [uiLanguage, storedFormat],
    );

    const prefs: DateFormatPrefs = useMemo(
        () => ({
            uiLanguage,
            dateFormat: storedFormat,
        }),
        [uiLanguage, storedFormat],
    );

    const dateFnsLocale = useMemo(() => getDateFnsLocale(uiLanguage), [uiLanguage]);
    const pattern = useMemo(() => dateFormatPattern(dateFormat), [dateFormat]);
    const pickerFormat = useMemo(() => pickerDateFormat(dateFormat), [dateFormat]);

    const formatShort = useCallback(
        (value: string | Date | null | undefined, empty = '—') =>
            formatDateDisplay(value, prefs, 'short', empty),
        [prefs],
    );

    const formatMedium = useCallback(
        (value: string | Date | null | undefined, empty = '—') =>
            formatDateDisplay(value, prefs, 'medium', empty),
        [prefs],
    );

    const formatLong = useCallback(
        (value: string | Date | null | undefined, empty = '—') =>
            formatDateDisplay(value, prefs, 'long', empty),
        [prefs],
    );

    const formatDateTime = useCallback(
        (value: string | Date | null | undefined, empty = '—') =>
            formatDateTimeDisplay(value, prefs, empty),
        [prefs],
    );

    const format = useCallback(
        (
            value: string | Date | null | undefined,
            style: DateDisplayStyle = 'medium',
            empty = '—',
        ) => formatDateDisplay(value, prefs, style, empty),
        [prefs],
    );

    const parseInput = useCallback(
        (text: string) => parseDateInput(text, dateFormat),
        [dateFormat],
    );

    return {
        /** Effective order after auto-resolution. */
        dateFormat: dateFormat as DateFormatOrder,
        /** Explicit stored preference (null = auto). */
        storedDateFormat: storedFormat,
        uiLanguage,
        prefs,
        dateFnsLocale,
        pattern,
        pickerDateFormat: pickerFormat,
        formatShort,
        formatMedium,
        formatLong,
        formatDateTime,
        format,
        parseInput,
    };
}

export { getDatePreferencesSnapshot };
